import type { RateCalculationTestRecord } from '../services/CarrierService/types';
import { strict as assert } from 'assert';
import { isValid } from 'date-fns';

const optionalStringToNumber = (value?: string) => {
  return value && !isNaN(+value) ? +value : undefined;
};

export const buildCarrierRateRequests = (rawRecords: string[][], partnerId: string, shipmentEffectiveDate: string) => {
  const records: RateCalculationTestRecord[] = [];
  if (rawRecords.length < 2) {
    return records;
  }
  const data = rawRecords.slice(1);
  let rowIndex = 1;

  for (const row of data) {
    try {
      const packagingMaterial = row[0].toLowerCase() as any;
      const weightUnit = row[6].toLowerCase() as any;
      const linearUnit = row[4].toLowerCase() as any;
      const ErrorDesc = `CSV Error (Row ${rowIndex + 1})`;
      const shipDateCol: string = row[23]?.trim();
      let shipDate: Date | null = null;
      const desiredDeliveryDateCol: string = row[24]?.trim();
      let desiredDeliveryDate: Date | null = null;
      const shipOption = row[25]?.trim();
      const businessDaysOfTransit = optionalStringToNumber(row[26]);
      const fulfillmentContext = row[27]?.trim();
      const productDetails = row[7].length > 0 ? row[7].split(',') : [];
      const limitedQuantity = row[28].toLowerCase() === 'true';
      const ormd = row[29].toLowerCase() === 'true';
      const hazmatInfo = row[31] && row[31].length > 0 ? JSON.parse(row[31]) : undefined;

      if (limitedQuantity) {
        productDetails.push('LIMITED_QUANTITY');
      }
      if (ormd) {
        productDetails.push('ORMD');
      }
      assert(
        ['box', 'flat_pack', 'envelope'].includes(packagingMaterial),
        `${ErrorDesc} Packaging Material: ${packagingMaterial}.  Valid values are: box, envelope, flat_pack.`,
      );

      assert(['lb', 'kg'].includes(weightUnit), `${ErrorDesc} Weight Unit: ${weightUnit}.  Valid values are: lb, kg`);

      assert(['in', 'cm'].includes(linearUnit), `${ErrorDesc} Linear Unit: ${linearUnit}.  Valid values are: in, cm`);

      assert(weightUnit);

      if (shipDateCol) {
        shipDate = new Date(shipDateCol);
        assert(isValid(shipDate), `${ErrorDesc} Desired Delivery Date: ${shipDateCol}.  Invalid Date.`);
      }
      if (desiredDeliveryDateCol) {
        desiredDeliveryDate = new Date(desiredDeliveryDateCol);
        assert(
          isValid(desiredDeliveryDate),
          `${ErrorDesc} Desired Delivery Date: ${desiredDeliveryDateCol}.  Invalid Date.`,
        );
      }
      if (businessDaysOfTransit) {
        assert(
          businessDaysOfTransit > 0 && businessDaysOfTransit <= 30,
          'Business Days of Transit must be between 1 and 30',
        );
      }

      records.push({
        rowIndex,
        expectedCost: +row[8],
        request: {
          partnerId,
          packagingMaterial,
          orderItemQuantities: [
            {
              productId: '1234',
              quantity: 1,
              productDetails,
              hazmat: row[30].toLowerCase() === 'true',
              hazmatInfo,
            },
          ],
          hazmatParams: {
            hazmat: row[30].toLowerCase() === 'true',
            ...(hazmatInfo || {}),
          },
          packagelinearDimensions: {
            length: +row[1],
            width: +row[2],
            height: +row[3],
            linearUnit,
          },
          packageWeight: {
            weight: +row[5],
            weightUnit,
          },
          unsupportedProductDetails: productDetails,
          shipmentEffectiveDate,
          ...(shipDate && { shipDate: shipDate.toISOString() }),
          ...(desiredDeliveryDate && { desiredDeliveryDate: desiredDeliveryDate.toISOString() }),
          ...(shipOption && { shipOption }),
          ...(businessDaysOfTransit && { businessDaysOfTransit }),
          ...(fulfillmentContext && { fulfillmentContext }),
          shipmentSourceAddress: {
            addressType: row[9] as any,
            address1: row[10],
            address2: row[11],
            city: row[12],
            state: row[13],
            postalCode: row[14].padStart(5, '0'),
            country: row[15],
          },
          shipmentDestinationAddress: {
            addressType: row[16] as any,
            address1: row[17],
            address2: row[18],
            city: row[19],
            state: row[20],
            postalCode: row[21].padStart(5, '0'),
            country: row[22],
          },
        },
      });
    } catch (e) {
      if (row[1] && row[14] && row[21]) {
        records.push({ rowIndex, errorMessage: `${e}` });
      }
    }

    rowIndex++;
  }
  return records;
};
